import { useGetMe } from 'api-hooks/auth/query';
import { CruiseSaleModel } from 'api-hooks/sale/cruise';
import { generateId } from 'common/utils/string';
import {
  guestNameTransformer,
  guestPreferencesTransformer,
} from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import React from 'react';

import {
  CruiseSaleCruiseSaleAddOnsFormType,
  CruiseSaleCruiseSaleDetailsFormType,
  CruiseSaleCruiseSaleFormType,
  CruiseSaleCruiseSaleGuestFormType,
  CruiseSaleFormType,
} from '../form-type';

interface Props {
  defaultValues?: CruiseSaleFormType;
  cruiseSale?: CruiseSaleModel;
}

export function addNewAddOnsItem(): CruiseSaleCruiseSaleAddOnsFormType {
  return {
    customId: generateId(),
    description: '',
    guestId: '',
    guestName: '',
    id: undefined,
    price: undefined,
  } as never as CruiseSaleCruiseSaleAddOnsFormType;
}

export function addNewGuestItem(): CruiseSaleCruiseSaleGuestFormType {
  return {
    customId: generateId(),
    guestId: '',
    guestName: '',
    guestPreferences: [],
    preferences: [],
    guestDetailId: '',
  } as never as CruiseSaleCruiseSaleGuestFormType;
}

export const addNewDetailsItem = () => {
  return {
    customId: generateId(),
    cruiseRoomId: '',
    cruiseRoomName: '',
    cruiseRoomNumber: '',
    deck: '',
    description: '',
    basicFare: undefined,
    id: undefined,
    portCharge: undefined,
    tax: undefined,
    tipping: undefined,
    guestId: '',
    guestName: '',
    guestDetailId: '',
  } as never as CruiseSaleCruiseSaleDetailsFormType;
};

export function addNewCruiseSaleItem(): CruiseSaleCruiseSaleFormType {
  return {
    bookingCode: '',
    cruiseId: '',
    cruiseName: '',
    cruiseShipId: '',
    cruiseShipName: '',
    cruiseSaleAddOns: [],
    cruiseSaleGuests: [addNewGuestItem()],
    cruiseSaleDetails: [addNewDetailsItem()],
    customId: generateId(),
    isGroupedPrice: true,
    reminder: false,
    reminderDate: null,
    reminderDescription: null,
    route: '',
    endedAt: new Date(),
    startedAt: new Date(),
    id: undefined,
  } as never as CruiseSaleCruiseSaleFormType;
}

export default function useGetDefaultValues(props: Props) {
  const { cruiseSale } = props;
  const { data: employeeData } = useGetMe();
  const employeeHook = employeeData?.data;
  const defaultValues = React.useMemo<CruiseSaleFormType>(() => {
    const transactionAt = cruiseSale?.transactionAt ?? new Date();

    if (props.defaultValues) {
      return {
        ...props.defaultValues,
        refId: undefined,
        invoiceNumber: undefined,
        tempTransactionAt: transactionAt,
        transactionAt,
        needApproval: false,
        cruiseSales: props.defaultValues.cruiseSales.map((cruiseSale) => ({
          ...cruiseSale,
          id: undefined,
          bookingCode: '',
          cruiseSaleGuests: cruiseSale.cruiseSaleGuests.map((guest) => ({
            ...guest,
            id: undefined,
          })),
          cruiseSaleDetails: cruiseSale.cruiseSaleDetails.map((detail) => ({
            ...detail,
            id: undefined,
          })),
          cruiseSaleAddOns: cruiseSale.cruiseSaleAddOns.map((addons) => ({
            ...addons,
            id: undefined,
          })),
        })),
      };
    }
    const customer = cruiseSale?.customer;
    return {
      address: customer?.address ?? '',
      adminId: cruiseSale?.admin.id ?? employeeHook?.id ?? '',
      code: cruiseSale?.code ?? '',
      cruiseSales: cruiseSale?.cruiseSales?.map((cs) => {
        const cruiseSaleGuests: CruiseSaleCruiseSaleGuestFormType[] =
          cs.cruiseSaleGuests.map((csg) => {
            return {
              customId: generateId(),
              guestId: csg.guest.id,
              guestName: guestNameTransformer(csg.guest),
              guestPreferences: guestPreferencesTransformer(csg.guest),
              id: csg.id,
              preferences: csg.preferences.map((csgp) => {
                return { id: csgp.id, label: csgp.name };
              }),
              guestDetailId: csg.guest.id,
            };
          });
        const cruiseSaleDetails: CruiseSaleCruiseSaleDetailsFormType[] =
          cs.cruiseSaleDetails.map((csd, idx) => {
            const guest = cs.cruiseSaleGuests.find(
              (csg) => csg.id === csd.cruiseSaleGuestId,
            );
            return {
              basicFare: csd.basicFare,
              cruiseRoomId: csd.cruiseRoom.id,
              cruiseRoomName: `${csd.cruiseRoom.type} (${csd.cruiseRoom.size})`,
              cruiseRoomNumber: csd.cruiseRoomNumber,
              customId: generateId(),
              deck: csd.deck,
              description: csd.description || '',
              guestId: guest?.guest.id || '',
              guestName: !cs.isGroupedPrice
                ? guestNameTransformer(guest!.guest)
                : '',
              id: csd.id,
              portCharge: csd.portCharge,
              tax: csd.tax,
              tipping: csd.tipping,
              guestDetailId: guest?.guest.id || '',
            };
          });
        const cruiseSaleAddOns: CruiseSaleCruiseSaleAddOnsFormType[] =
          cs.cruiseSaleAddOns.map((csao) => {
            return {
              id: csao.id,
              description: csao.description || '',
              price: csao.price,
              guestId: csao.guest.id,
              guestName: csao.guest.name,
              customId: generateId(),
            };
          });
        return {
          bookingCode: cs.bookingCode || '',
          cruiseId: cs.cruise.id,
          cruiseName: cs.cruise.name,
          cruiseShipId: cs.cruiseShip?.id || '',
          cruiseShipName: cs.cruiseShip?.name || '',
          cruiseSaleGuests,
          cruiseSaleDetails,
          cruiseSaleAddOns,
          customId: generateId(),
          endedAt: cs.endedAt,
          startedAt: cs.startedAt,
          isGroupedPrice: cs.isGroupedPrice,
          reminder: cs.reminder,
          reminderDate: cs.reminderDate || null,
          reminderDescription: cs.reminderDescription || '',
          id: cs.id,
          route: cs.route || '',
        };
      }) ?? [addNewCruiseSaleItem()],
      invoiceNumber: cruiseSale?.invoiceNumber || '',
      customerBillingId: cruiseSale?.customerBillingAddress?.id || '',
      customerBillingOptions: [],
      customerId: customer?.id || '',
      customerStatus: customer?.status || '',
      description: cruiseSale?.description || '',
      discount: cruiseSale?.discount || 0,
      hasDueReceivable: customer?.hasDueReceivables ?? false,
      remainingTransactionQuota: null,
      isExceedingQuota: false,
      needApproval: false,
      number: cruiseSale?.invoiceNumber || '',
      operatorId: cruiseSale?.operator?.id || '',
      otherCost: cruiseSale?.otherCost || 0,
      phone: customer?.phone || '',
      refId: cruiseSale?.refId,
      parentRefId: cruiseSale?.parentSale?.refId,
      salesmanId: cruiseSale?.salesman?.id || '',
      tempTransactionAt: transactionAt,
      transactionAt,
      total: cruiseSale?.total || 0,
      cruiseSale,
    };
  }, [cruiseSale, employeeHook?.id, props.defaultValues]);

  return defaultValues;
}
